import {
    Button,
    IconBox,
    IconBoxList,
    Section,
    Tab,
    Tabs,
    QuantitySelector,
    ModalDialog,
    ModalOverlay,
    type SelectedIconBoxProps,
    LoadingDots
} from '@flos/react-ui';
import { useStartOnsaFlow } from '../../hooks/useStartOnsaFlow';
import { useCallback, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { usePreliminaryQuestions } from '../../hooks/usePreliminaryQuestions';
import { categories, type Category, type ProductKey, products } from './ProductSelectorConfig';
import { externalLinks } from '../../strings';
import type { ProductSelectorDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto';
import { useHippoText } from '../../hooks/useHippoText';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { onsaProductStatusListAtom } from '../../recoil/atoms/onsaProductStatusListAtom';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import type {
    AllCoverablesDTO,
    PreliminaryQuestionsDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote/';
import { useGetVIDCustomerStop } from '../../recoil/hooks/useGetVIDCustomerStop';
import { topConfigContextAtom } from '../../recoil/atoms/topConfigContextAtom';
import CustomerStopModal from '../../shared_components/CustomerStopModal/CustomerStopModal';
import type { CustomerStopCause } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { getPrimaryAccountNumber } from '../../utils/topConfigContextUtil';
import { isLoggedOnSelector } from '../../recoil/selectors/isLoggedOnSelector';
import { track } from '../../utils/trackingUtil';
import { jobAtom } from '../../recoil/atoms/jobAtom';
import { DRAFT } from '../../recoil/types/types';
import type { AllCoverablesOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import {
    allCoverablesOnsaToCoverableDTOArray,
    getPropertyToUpdate,
    updateProductStatusList
} from '../../utils/coverablesUtil';
import _ from 'lodash';
import { isPreliminaryQuestionSavedAtom } from '../../recoil/atoms/isPreliminaryQuestionSavedAtom';
import { useAsyncRecoilValue } from '../../hooks/useAsyncRecoilValue';
import { allHippoCoverageTextsSelector } from '../../recoil/selectors/allHippoCoverageTextsSelector';

const flavourMap: Record<string, string> = {
    ulykkesforsikring: 'accident',
    borneforsikring: 'childAccident',
    børneforsikring: 'childAccident',
    rejseforsikring: 'travel',
    indboforsikring: 'privateContent',
    bilforsikring: 'car',
    katteforsikring: 'cat',
    hundeforsikring: 'dog',
    'ulykkesforsikring til unge': 'youngAccident',
    'rejseforsikring til enkeltperson': 'travel',
    'indboforsikring til unge': 'youthContent'
};

const preliminaryProductMap: Record<string, string> = {
    ulykkesforsikring: 'adultAccident',
    borneforsikring: 'childInsurance',
    rejseforsikring: 'travel',
    indboforsikring: 'privateContent',
    bilforsikring: 'car',
    katteforsikring: 'cat',
    hundeforsikring: 'dog'
};

interface QuantityChange {
    multiple: boolean;
    selected?: boolean;
    quantity: number;
}

const getInitialProductList = (
    allCoverables: AllCoverablesDTO,
    allCoverablesOnsa: AllCoverablesOnsaDTO
): ProductSelector => {
    return {
        dog: (allCoverables.dogs ?? []).length + (allCoverablesOnsa.dogs ?? []).length,
        cat: (allCoverables?.cats ?? []).length + (allCoverablesOnsa.cats ?? []).length,
        travel: (allCoverables?.travels ?? []).length + (allCoverablesOnsa?.travels ?? []).length,
        car: (allCoverables?.cars ?? []).length + (allCoverablesOnsa?.cars ?? []).length,
        accident:
            (allCoverables?.adultAccidents ?? []).length +
            (allCoverablesOnsa?.adultAccidents ?? []).length,
        youngAccident:
            (allCoverables?.youngAccidents ?? []).length +
            (allCoverablesOnsa?.youngAccidents ?? []).length,
        privateContent:
            (allCoverables?.privateContent ?? []).length +
            (allCoverablesOnsa?.privateContent ?? []).length,
        childAccident:
            (allCoverables?.childAccidents ?? []).length +
            (allCoverablesOnsa?.childAccidents ?? []).length,
        youthContent:
            (allCoverables?.youthContent ?? []).length +
            (allCoverablesOnsa?.youthContent ?? []).length,
        house: 0,
        holidayHome: 0,
        salaryProtection: 0,
        horse: 0,
        singleTripTravel: 0,
        singleTripTravelCancellation: 0,
        motorcycle: 0,
        boat: 0,
        moped: 0,
        caravan: 0,
        changeOfOwnership: 0
    };
};

const isProductInAllCoverables = (productId: string, coverables: AllCoverablesDTO): boolean => {
    const productFlavour = Object.keys(flavourMap)[Object.values(flavourMap).indexOf(productId)];
    const formattedKey = getPropertyToUpdate(
        productFlavour.charAt(0).toUpperCase() + productFlavour.slice(1)
    ) as keyof typeof coverables;
    const product = coverables[formattedKey] ?? [];
    return product.length > 0;
};

type ProductSelector = Record<ProductKey, number>;

const ProductSelectorPage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const setJobDTO = useSetRecoilState(jobAtom);
    const allCoverables = useRecoilValue(allCoverablesAtom);
    const [allCoverablesOnsa, setAllCoverablesOnsa] = useRecoilState(allCoverablesOnsaAtom);
    const initialProductList = getInitialProductList(allCoverables, allCoverablesOnsa);
    const { startOnsaFlow } = useStartOnsaFlow();
    const { getDataFromQueryParams } = usePreliminaryQuestions();
    const getHippoText = useHippoText();
    const preliminaryQuestions = getDataFromQueryParams();
    const preliminaryProduct = preliminaryQuestions.flavourToUpdate ?? '';
    const convertedProduct = flavourMap[preliminaryProduct];
    const numberOfChildren = preliminaryQuestions?.childInsurance?.numberOfChildren;
    const verifiedNumberOfChildren =
        numberOfChildren !== undefined && !isNaN(numberOfChildren) ? numberOfChildren : 1;
    const [showCallMeModal, setShowCallMeModal] = useState(false);
    const [isPreliminaryQuestionLoading, setIsPreliminaryQuestionLoading] =
        useState<boolean>(false);
    const [isPreliminaryQuestionSaved, setIsPreliminaryQuestionSaved] = useRecoilState(
        isPreliminaryQuestionSavedAtom
    );
    const [productList, setProductList] = useState<ProductSelector>(() => {
        if (Object.keys(flavourMap).includes(preliminaryProduct) && !isPreliminaryQuestionSaved) {
            if (products[convertedProduct as ProductKey].quantitySelector) {
                return {
                    ...initialProductList,
                    [convertedProduct]:
                        initialProductList[convertedProduct as ProductKey] +
                        (convertedProduct === 'childAccident' ? verifiedNumberOfChildren : 1)
                };
            } else {
                return {
                    ...initialProductList,
                    [convertedProduct]: 1
                };
            }
        }
        return initialProductList;
    });
    const [showStopModal, setShowStopModal] = useState<boolean>(false);
    const [stopCause, setStopCause] = useState<CustomerStopCause>('None');
    const topConfigContext = useRecoilValue(topConfigContextAtom);
    const { getVIDCustomerStop } = useGetVIDCustomerStop();
    const isLoggedOn = useRecoilValue(isLoggedOnSelector);
    const [productStatusList, setProductStatusList] = useRecoilState(onsaProductStatusListAtom);
    const hippoTexts = {
        productAlreadyInBasket: getHippoText('DSS-PRODUCT-SELECTOR-ALREADY-IN-BASKET'),
        calculate: getHippoText('DSS-PRODUCT-SELECTOR-CALCULATE')
    };
    const isProductSelected = Object.values(productList).some((count) => count > 0);

    const isAnyNewProductSelected = Object.entries(productList).some(
        ([key, value]) => value !== initialProductList[key as ProductKey]
    );

    const areAllProductsCalculated = Object.values(productStatusList).every((products) =>
        products.every((product) => product.calculated)
    );
    const [previousSelection, setPreviousSelection] = useState<SelectedIconBoxProps[]>();

    // Prefetch coverage hippo texts for existing basket
    useAsyncRecoilValue(allHippoCoverageTextsSelector);

    useEffect(() => {
        if (isLoggedOn) return;
        const convertedFlavour = preliminaryProductMap[preliminaryProduct];
        const product = preliminaryQuestions[convertedFlavour as keyof PreliminaryQuestionsDTO];
        const hasPreliminaryQuestions = product
            ? Object.entries(product).some(
                  ([key, value]) => key !== 'flavourToUpdate' && value !== undefined && value !== ''
              )
            : false;
        if (hasPreliminaryQuestions && !isPreliminaryQuestionSaved) {
            setIsPreliminaryQuestionLoading(true);
            void startOnsaAndRedirect().then(() => {
                setIsPreliminaryQuestionLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const convertToProductSelectorDTO = (productList: ProductSelector): ProductSelectorDTO => {
        return {
            travel: productList.travel,
            accident: productList.accident,
            car: productList.car,
            cat: productList.cat,
            youngAccident: productList.youngAccident,
            dog: productList.dog,
            privateContent: productList.privateContent,
            childAccident: productList.childAccident,
            kennel: 0,
            youthContent: productList.youthContent
        };
    };

    const handleCardClick = useCallback(
        (selected: SelectedIconBoxProps[]): void => {
            setPreviousSelection(selected);
            if (!previousSelection) {
                const firstAddedProduct = selected.find(
                    (product) => productList[product.id as ProductKey] === 0
                );
                if (firstAddedProduct) {
                    setProductList((prev) => ({
                        ...prev,
                        [firstAddedProduct.id]: prev[firstAddedProduct.id as ProductKey] + 1
                    }));
                } else {
                    setProductList(initialProductList);
                }
                return;
            }

            const addedProduct = selected.find(
                (product) =>
                    !previousSelection.some(({ id }) => id === product.id) &&
                    productList[product.id as ProductKey] === 0
            );
            if (addedProduct) {
                setProductList((prev) => ({
                    ...prev,
                    [addedProduct.id]: prev[addedProduct.id as ProductKey] + 1
                }));
                return;
            }

            const removedProduct = previousSelection.find(
                (product) => !selected.some(({ id }) => id === product.id)
            );
            if (removedProduct) {
                setProductList((prev) => ({
                    ...prev,
                    [removedProduct.id]: prev[removedProduct.id as ProductKey] - 1
                }));
            }
        },
        [previousSelection, productList, initialProductList]
    );

    const onChangeQuantity = (selected: QuantityChange, id: string): void => {
        setProductList((prevList) => ({
            ...prevList,
            [id]: selected.quantity
        }));
        if (selected.quantity === 0 && previousSelection) {
            setPreviousSelection(previousSelection.filter((product) => product.id !== id));
        }
    };

    const getQuantitySelector = (id: keyof ProductSelector): React.JSX.Element => (
        <QuantitySelector
            selectable={!isLoading}
            initialValue={productList[id]}
            multiple
            selected={productList[id] > 0}
            onChange={(selected) => {
                onChangeQuantity(selected, id);
            }}
            min={isLoading ? 100 : initialProductList[id]}
            max={isLoading ? 0 : 50}
        />
    );

    const handleClose = (): void => {
        // TODO. Replace mock with logic for OPC
        const shouldReturnToBasket = false;
        if (shouldReturnToBasket) {
            wizardGotoStep('ShoppingBasketPage');
        } else {
            window.open(externalLinks.topdanmarkDK, '_self');
        }
    };

    const removeCalculatedProducts = (): ProductSelector => {
        const updatedProductList: ProductSelector = { ...productList };

        for (const products of Object.values(productStatusList)) {
            for (const product of products) {
                if (product.calculated && product.flavourName.toLowerCase() in flavourMap) {
                    const flavourName = product.flavourName.toLowerCase();
                    const productName = flavourMap[flavourName] as ProductKey;
                    if (productName in updatedProductList && updatedProductList[productName] > 0) {
                        updatedProductList[productName]--;
                    }
                }
            }
        }

        return updatedProductList;
    };

    const removeExistingOnsaProducts = (productList: ProductSelector): ProductSelector => {
        const updatedProductList: ProductSelector = { ...productList };
        const existingOnsaProducts = allCoverablesOnsaToCoverableDTOArray(allCoverablesOnsa);
        for (const product of existingOnsaProducts) {
            if (!product.flavourName) continue;
            const flavourName = product.flavourName.toLowerCase();
            const productName = flavourMap[flavourName] as ProductKey;
            updatedProductList[productName] -= 1;
        }
        return updatedProductList;
    };

    const checkVIDCustomerStop = async (): Promise<boolean> => {
        if (!isLoggedOn) {
            return false;
        }
        if (stopCause !== 'None') {
            setShowStopModal(true);
            return true;
        }
        const accountNumber = getPrimaryAccountNumber(topConfigContext);
        const response = await getVIDCustomerStop(accountNumber);
        const stop = response.stopCause;
        if (stop !== 'None') {
            setStopCause(stop);
            setShowStopModal(true);
            return true;
        }
        return false;
    };

    const startOnsaAndRedirect = async (): Promise<void> => {
        if (stopCause !== 'None') {
            setShowStopModal(true);
            return;
        }
        const productListWithoutCalculated = removeCalculatedProducts();
        const productListWithoutExistingOnsa = removeExistingOnsaProducts(
            productListWithoutCalculated
        );
        try {
            const newOnsaCoverables = await startOnsaFlow(
                convertToProductSelectorDTO(productListWithoutExistingOnsa),
                !isPreliminaryQuestionSaved ? preliminaryQuestions : {}
            );

            const updatedProductList = updateProductStatusList(
                productStatusList,
                newOnsaCoverables
            );
            const trackSelectedProducts = Object.values(updatedProductList)
                .filter((value) => value.length > 0)
                .map((value) => ({
                    [value[0].flavourName]: value.length
                }));
            setJobDTO((jobDTO) => ({ ...jobDTO, jobStatus: DRAFT }));
            track('SelectedProducts', trackSelectedProducts);
            setProductStatusList(updatedProductList);
            setAllCoverablesOnsa((existingOnsaCoverables) =>
                _.mergeWith(
                    newOnsaCoverables,
                    existingOnsaCoverables,
                    (newProducts = [], existingProducts = []) => [
                        ...newProducts,
                        ...existingProducts
                    ]
                )
            );
            setIsPreliminaryQuestionSaved(true);
            const shouldBlockCustomer = await checkVIDCustomerStop();
            if (shouldBlockCustomer) return;
            wizardGotoStep('CoverableInformationPage');
        } catch {
            setShowCallMeModal(true);
        }
    };

    const onNextClick = async (): Promise<void> => {
        setIsLoading(true);

        if (!isAnyNewProductSelected && areAllProductsCalculated) {
            setProductStatusList({});
            wizardGotoStep('ShoppingBasketPage');
            setIsLoading(false);
            return;
        }

        const unsupportedProducts = Object.entries(productList).filter(
            ([productKey, numOfSelected]) =>
                numOfSelected > 0 && !products[productKey as ProductKey].supported
        );

        if (Object.keys(unsupportedProducts).length > 0) {
            setShowCallMeModal(true);
            setIsLoading(false);
            return;
        }

        await startOnsaAndRedirect();
        setIsLoading(false);
    };

    const getSelectedItems = (category: Category): SelectedIconBoxProps[] => {
        const selectedProducts: SelectedIconBoxProps[] = [];
        category.products
            .filter((productId: ProductKey) => productList[productId] > 0)
            .forEach((productId: ProductKey) => {
                const product = products[productId];
                selectedProducts.push({
                    title: product.title,
                    id: productId,
                    selected: true
                });
            });
        return selectedProducts;
    };

    if (isPreliminaryQuestionLoading) {
        return <LoadingDots />;
    }

    return (
        <>
            <ModalOverlay isOpen={true} isFullScreen>
                <ModalDialog isFullScreen closeIconHandler={handleClose}>
                    <Section className="u-bg-white">
                        <h4 className="u-Center">Hvilken forsikring vil du have en pris på?</h4>
                        <Tabs variant="IconBox">
                            {categories.map((category) => (
                                <Tab
                                    key={category.label}
                                    label={category.label}
                                    iconshape={category.iconShape}
                                >
                                    <IconBoxList
                                        onSelect={handleCardClick}
                                        selectedItems={getSelectedItems(category)}
                                    >
                                        {category.products.map((productId) => {
                                            const product = products[productId];
                                            if (!product) {
                                                return undefined;
                                            }
                                            return (
                                                <IconBox
                                                    key={productId}
                                                    label={
                                                        !product.quantitySelector &&
                                                        productList[productId] &&
                                                        isProductInAllCoverables(
                                                            productId,
                                                            allCoverables
                                                        )
                                                            ? hippoTexts.productAlreadyInBasket
                                                            : undefined
                                                    }
                                                    selectable={!isLoading}
                                                    id={productId}
                                                    title={product.title}
                                                    iconShape={product.iconShape}
                                                    data-testid={`${productId}-id`}
                                                    quantitySelector={
                                                        product.quantitySelector
                                                            ? getQuantitySelector(productId)
                                                            : undefined
                                                    }
                                                    selected={productList[productId] > 0}
                                                />
                                            );
                                        })}
                                    </IconBoxList>
                                </Tab>
                            ))}
                        </Tabs>
                        <div className="u-spacing-stack-11">
                            <div
                                className="u-border-top-secondary-4-base u-bg-white"
                                style={{
                                    width: '100%',
                                    position: 'fixed',
                                    bottom: 0,
                                    left: 0,
                                    zIndex: 1
                                }}
                            >
                                <div className="container">
                                    <div className="u-spacing-inset-l--stretch-max u-flex-box u-flex-box--center">
                                        <Button
                                            onClick={() => {
                                                void onNextClick();
                                            }}
                                            disabled={!isProductSelected || isLoading}
                                            isLoading={isLoading}
                                        >
                                            {hippoTexts.calculate}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Section>
                </ModalDialog>
            </ModalOverlay>
            {/* TODO: Modify call me information for unsupported products */}
            <CallMeModal
                title={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-HEADER')}
                description={getHippoText('DSS-CALL-ME-MODAL-NO-PRICE-SUBHEADER')}
                activityCode=""
                showModal={showCallMeModal}
                closeModal={() => {
                    setShowCallMeModal(false);
                }}
            />
            <CustomerStopModal
                stopCause={stopCause}
                showModal={showStopModal}
                closeModal={() => {
                    setShowStopModal(false);
                }}
            />
        </>
    );
};

export default ProductSelectorPage;
